/**
 * cycleAnchors.ts — Deterministic, archetype-aware cycle anchor resolution.
 * 
 * Replaces arbitrary edge-iteration-order selection with logic driven by
 * the operation archetype and model fields that are guaranteed to exist.
 * 
 * TRANSFER: entry = sourceMaterialId, exit = destinationPostMaterialId (+ fallbacks)
 * COMPLEX:  entry/exit = input/output with largest total IO fraction
 * TRANSFER / MEASURE: the single input / output (trivial)
 * 
 * Every fallback sorts candidates by creationOrder (if available) then id
 * to ensure deterministic, reproducible results independent of key iteration order.
 */

import type {
  CycleRange,
  GroupSpec,
  OperationNodeSpec,
  MaterialNodeSpec,
  IOMapping,
} from './types';

export interface GraphData {
  materialNodes: Record<string, MaterialNodeSpec>;
  operationNodes: Record<string, OperationNodeSpec>;
  flowEdges: Record<string, any>;
  groups: Record<string, GroupSpec>;
}

export interface ResolvedCycleAnchors {
  entryMaterialId: string;
  exitMaterialId: string;
}

/**
 * Resolve entry and exit material anchors for a cycle, using archetype-specific logic.
 * 
 * Returns null if either anchor is unresolvable (e.g., missing material reference,
 * or the start/end operations do not exist).
 * 
 * @param cycle The cycle range definition
 * @param group The group containing the cycle
 * @param graphData The full graph state
 */
export function resolveCycleAnchors(
  cycle: CycleRange,
  group: GroupSpec,
  graphData: GraphData,
): ResolvedCycleAnchors | null {
  const { startIndex, endIndex } = cycle;

  // Get the start and end operation IDs from the group's step list
  const startOpId = group.stepIds[startIndex];
  const endOpId = group.stepIds[endIndex];

  if (!startOpId || !endOpId) {
    return null;
  }

  const startOp = graphData.operationNodes[startOpId];
  const endOp = graphData.operationNodes[endOpId];

  if (!startOp || !endOp) {
    return null;
  }

  // Resolve entry anchor (inputs to the start step)
  const entryMaterialId = resolveEntryAnchor(startOp, graphData);
  // Resolve exit anchor (outputs from the end step)
  const exitMaterialId = resolveExitAnchor(endOp, graphData);

  if (!entryMaterialId || !exitMaterialId) {
    return null;
  }

  return { entryMaterialId, exitMaterialId };
}

/**
 * Resolve the entry (input) material anchor for an operation.
 */
function resolveEntryAnchor(
  operation: OperationNodeSpec,
  graphData: GraphData,
): string | null {
  const archetype = operation.archetype;

  if (archetype === 'TRANSFER') {
    // TRANSFER: try sourceMaterialId, then destinationMaterialId
    const transferRow = operation.transferRow;
    if (transferRow?.sourceMaterialId) {
      if (materialExists(transferRow.sourceMaterialId, graphData)) {
        return transferRow.sourceMaterialId;
      }
    }
    if (transferRow?.destinationMaterialId) {
      if (materialExists(transferRow.destinationMaterialId, graphData)) {
        return transferRow.destinationMaterialId;
      }
    }
    // Fallback: find any input to this operation
    return findInputMaterial(operation.id!, graphData);
  }

  if (archetype === 'COMPLEX') {
    // COMPLEX: find the input with the largest total fraction
    const complexOptions = operation.complexOptions;
    if (complexOptions?.ioMapping && complexOptions.ioMapping.length > 0) {
      // Group fractions by input material
      const inputFractions = new Map<string, number>();
      for (const mapping of complexOptions.ioMapping) {
        const total = (inputFractions.get(mapping.inputMaterialId) ?? 0) + mapping.fraction;
        inputFractions.set(mapping.inputMaterialId, total);
      }

      // Find the input with max fraction
      let maxInput: string | null = null;
      let maxFraction = 0;
      for (const [materialId, fraction] of inputFractions) {
        if (materialExists(materialId, graphData) && fraction > maxFraction) {
          maxInput = materialId;
          maxFraction = fraction;
        }
      }

      if (maxInput) {
        return maxInput;
      }
    }
    // Fallback: find any input to this operation
    return findInputMaterial(operation.id!, graphData);
  }

  if (archetype === 'TRANSFORM' || archetype === 'MEASURE') {
    // Single input — find it
    return findInputMaterial(operation.id!, graphData);
  }

  return null;
}

/**
 * Resolve the exit (output) material anchor for an operation.
 */
function resolveExitAnchor(
  operation: OperationNodeSpec,
  graphData: GraphData,
): string | null {
  const archetype = operation.archetype;

  if (archetype === 'TRANSFER') {
    // TRANSFER: try destinationPostMaterialId (merged), then sourcePostMaterialId (remainder)
    const transferRow = operation.transferRow;
    if (transferRow?.destinationPostMaterialId) {
      if (materialExists(transferRow.destinationPostMaterialId, graphData)) {
        return transferRow.destinationPostMaterialId;
      }
    }
    if (transferRow?.sourcePostMaterialId) {
      if (materialExists(transferRow.sourcePostMaterialId, graphData)) {
        return transferRow.sourcePostMaterialId;
      }
    }
    // Fallback: find any output from this operation
    return findOutputMaterial(operation.id!, graphData);
  }

  if (archetype === 'COMPLEX') {
    // COMPLEX: find the output with the largest total fraction
    const complexOptions = operation.complexOptions;
    if (complexOptions?.ioMapping && complexOptions.ioMapping.length > 0) {
      // Group fractions by output material
      const outputFractions = new Map<string, number>();
      for (const mapping of complexOptions.ioMapping) {
        const total = (outputFractions.get(mapping.outputMaterialId) ?? 0) + mapping.fraction;
        outputFractions.set(mapping.outputMaterialId, total);
      }

      // Find the output with max fraction
      let maxOutput: string | null = null;
      let maxFraction = 0;
      for (const [materialId, fraction] of outputFractions) {
        if (materialExists(materialId, graphData) && fraction > maxFraction) {
          maxOutput = materialId;
          maxFraction = fraction;
        }
      }

      if (maxOutput) {
        return maxOutput;
      }
    }
    // Fallback: find any output from this operation
    return findOutputMaterial(operation.id!, graphData);
  }

  if (archetype === 'TRANSFORM' || archetype === 'MEASURE') {
    // Single output — find it
    return findOutputMaterial(operation.id!, graphData);
  }

  return null;
}

/**
 * Find an input material to an operation, sorted by creationOrder then id.
 */
function findInputMaterial(opId: string, graphData: GraphData): string | null {
  const candidates: Array<{ id: string; order: number }> = [];

  for (const edge of Object.values(graphData.flowEdges)) {
    if (edge.targetId === opId) {
      const material = graphData.materialNodes[edge.sourceId];
      if (material && material.id) {
        candidates.push({
          id: edge.sourceId,
          order: material.createdAt ? new Date(material.createdAt).getTime() : Infinity,
        });
      }
    }
  }

  if (candidates.length === 0) {
    return null;
  }

  // Sort by creationOrder if available, else by id
  candidates.sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.id.localeCompare(b.id);
  });

  return candidates[0].id;
}

/**
 * Find an output material from an operation, sorted by creationOrder then id.
 */
function findOutputMaterial(opId: string, graphData: GraphData): string | null {
  const candidates: Array<{ id: string; order: number }> = [];

  for (const edge of Object.values(graphData.flowEdges)) {
    if (edge.sourceId === opId) {
      const material = graphData.materialNodes[edge.targetId];
      if (material && material.id) {
        candidates.push({
          id: edge.targetId,
          order: material.createdAt ? new Date(material.createdAt).getTime() : Infinity,
        });
      }
    }
  }

  if (candidates.length === 0) {
    return null;
  }

  // Sort by creationOrder if available, else by id
  candidates.sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.id.localeCompare(b.id);
  });

  return candidates[0].id;
}

/**
 * Check if a material exists in the graph.
 */
function materialExists(materialId: string, graphData: GraphData): boolean {
  return materialId in graphData.materialNodes;
}
